import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { HStack } from "../../../styled-system/jsx";
import { Modal } from "../../components/core/basic/Modal";
import { Button } from "../../components/core/basic/Button";
import { Combobox, ComboboxLoadMoreItem } from "../../components/fields/base/Combobox";
import { useAppSelector } from "../../redux/hooks";
import { useChangeOrg } from "../../hooks/useChangeOrg";
import { getStaffOrganizationsInfiniteOptions } from "./api/useStaffOrganizations";

export const ChangeOrgModal = NiceModal.create(() => {
  const { t } = useTranslation(["admin", "common"]);
  const modal = useModal();
  const user = useAppSelector((s) => s.user);
  const changeOrg = useChangeOrg();

  const [search, setSearch] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  const orgsQuery = useInfiniteQuery(
    getStaffOrganizationsInfiniteOptions({ search: debouncedSearch, enabled: modal.visible }),
  );

  const options = useMemo(() => {
    const pages = orgsQuery.data?.pages ?? [];
    return pages
      .flatMap((page) => page.results)
      .map((o) => ({
        value: o.id,
        label: o.name === o.profile_name ? `${o.name} (ID: ${o.id})` : `${o.name} — ${o.profile_name} (ID: ${o.id})`,
      }));
  }, [orgsQuery.data]);

  const isUnchanged = selectedOrgId == null || selectedOrgId === Number(user.organization_id);

  return (
    <Modal
      isOpen={modal.visible}
      onOpenChange={(open) => {
        if (!open) modal.remove();
      }}
      title={t(($) => $.admin["Change Organizations"])}
      css={{ padding: "xlarge", width: { base: "90vw", md: "50vw" } }}
    >
      <Combobox
        aria-label={t(($) => $.admin.Organization)}
        items={options}
        inputValue={search}
        onInputChange={(value) => {
          setSearch(value);
          if (value === "") setSelectedOrgId(null);
        }}
        value={selectedOrgId}
        onChange={(key) => {
          const nextId =
            typeof key === "number" ? key
            : key != null ? Number(key)
            : null;
          setSelectedOrgId(nextId);
          const selected = nextId != null ? options.find((o) => o.value === nextId) : null;
          setSearch(selected?.label ?? "");
        }}
        defaultFilter={() => true}
        isLoading={orgsQuery.isFetching && !orgsQuery.isFetchingNextPage}
        placeholder={t(($) => $.admin["Currently: {name} — search to switch"], { name: user.organization_name })}
        listBoxFooter={
          orgsQuery.hasNextPage ?
            <ComboboxLoadMoreItem
              isLoading={orgsQuery.isFetchingNextPage}
              onLoadMore={() => {
                if (!orgsQuery.isFetchingNextPage) {
                  void orgsQuery.fetchNextPage();
                }
              }}
            />
          : undefined
        }
      />
      <HStack justifyContent="center" gap="medium" marginTop="large">
        <Button variant="secondary" onPress={() => modal.remove()}>
          {t(($) => $.common.actions.Cancel)}
        </Button>
        <Button
          variant="primary"
          isPending={changeOrg.isPending}
          isDisabled={isUnchanged || changeOrg.isPending}
          onPress={() => selectedOrgId != null && changeOrg.mutate(selectedOrgId)}
        >
          {t(($) => $.admin.Change)}
        </Button>
      </HStack>
    </Modal>
  );
});
